// The real estate layer of the business locator: RE listings drawn as circle markers next to the businesses,
// grouped under "Development" in the category panel. These are layer categories, not macro types.
export const RE_LAYER_SEARCH_TYPE = "RE_LAYER";
// the category that shows RELocatorFilters
export const RE_LAYER_AVAILABLE = "re-available";

export const RE_LAYER_CATEGORIES = [
  {
    key: RE_LAYER_AVAILABLE,
    label: "Available spaces",
    color: "#F28C28",
    icon: "building",
    match: properties =>
      properties.saleOrLease === "SALE" || properties.saleOrLease === "LEASE"
  },
  {
    key: "re-development",
    label: "Under development",
    color: "#FFC107",
    icon: "person-digging",
    match: properties => properties.saleOrLease === "UNDER_DEVELOPMENT"
  }
];

export const reLayerCategoryFor = key =>
  RE_LAYER_CATEGORIES.find(category => category.key === key) ?? null;

// the search option a layer category selects (see LocatorContainer's selectedSearchOption)
export const reLayerSearchOption = category => ({
  searchType: RE_LAYER_SEARCH_TYPE,
  value: category.key,
  label: category.label
});

// re.geojson features restyled as category markers: markerRef draws the icon pin (LOCATOR_ICONS in MapBoxMap), and the
// price labels of the RE locator are dropped. Listings matching no category are left out.
export const decorateRELayerFeatures = geoJson =>
  (geoJson?.features ?? []).flatMap(feature => {
    const category = RE_LAYER_CATEGORIES.find(c => c.match(feature.properties));
    if (!category) return [];
    const { iconLabel, ...properties } = feature.properties;
    return [
      {
        ...feature,
        properties: {
          ...properties,
          reListing: true,
          reLayerCategory: category.key,
          hexColor: category.color,
          markerRef: `${category.color.replace("#", "")}/${category.icon}`
        }
      }
    ];
  });

// RELocatorFilters' filter applied to re.geojson features
export const filterREFeatures = (features, filter) => {
  const {
    saleOrLease,
    category,
    minAreaSqFt,
    maxAreaSqFt,
    minPrice,
    maxPrice,
    selectedAmenities
  } = filter ?? {};
  return (features ?? []).filter(feature => {
    const props = feature.properties;

    // Filter by saleOrLease
    if (saleOrLease && props.saleOrLease !== saleOrLease) {
      return false;
    }

    // Filter by category (unitType)
    if (category && props.unitType !== category) {
      return false;
    }

    // Filter by minAreaSqFt
    if (
      minAreaSqFt &&
      (typeof props.areaSqFt !== "number" || props.areaSqFt < minAreaSqFt)
    ) {
      return false;
    }

    // Filter by maxAreaSqFt
    if (
      maxAreaSqFt &&
      (typeof props.areaSqFt !== "number" || props.areaSqFt > maxAreaSqFt)
    ) {
      return false;
    }

    // Filter by price for SALE
    if (saleOrLease === "SALE") {
      if (
        minPrice &&
        (typeof props.salePrice !== "number" || props.salePrice < minPrice)
      ) {
        return false;
      }
      if (
        maxPrice &&
        (typeof props.salePrice !== "number" || props.salePrice > maxPrice)
      ) {
        return false;
      }
    }

    // Filter by price for LEASE
    if (saleOrLease === "LEASE") {
      if (
        minPrice &&
        (typeof props.monthlyRent !== "number" || props.monthlyRent < minPrice)
      ) {
        return false;
      }
      if (
        maxPrice &&
        (typeof props.monthlyRent !== "number" || props.monthlyRent > maxPrice)
      ) {
        return false;
      }
    }

    // Filter by selectedAmenities (at least one should match)
    if (
      selectedAmenities &&
      Array.isArray(selectedAmenities) &&
      selectedAmenities.length > 0
    ) {
      const featureAmenities = props.selectedAmenities || [];
      const hasAnyAmenity = selectedAmenities.some(a =>
        featureAmenities.includes(a)
      );
      if (!hasAnyAmenity) {
        return false;
      }
    }

    return true;
  });
};

// features -> the result items LocatorSearchResultsComponent lists in "re" mode
export const buildREResults = features =>
  (features ?? []).map(feature => ({
    ...feature.properties,
    location: {
      longitude: feature.properties.longitude,
      latitude: feature.properties.latitude,
      streetAddress: feature.properties.addressLine1
    },
    id: feature.properties.id
  }));
